import { createSelector } from 'reselect';
import {
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getAssetManagerShowPlanView,
  getAssetStatusesKeyedById,
  getAssetTypes,
  getCurrentPlanBandsForCharts,
  getLayers,
  getLayersKeyedByAncestorId,
  getPlannedTwinIDsKeyedByTwinId,
  getPrimaryLayers,
  getSelectedAssetType,
  getLocationSummarySlotConfig,
} from '..';
import {
  keyAssetsByStatus,
  getSelectedLayerAncestors,
  getEffectiveLayerIDs,
} from '../../utils';
import { getFilteredAssets, getFilteredAssetsContext, getAssetsKeyedByBandId } from '.';
import map from '../map';
import { Asset } from '../../config/types';
import { UNLOCATED_LAYER_LABEL } from '../../config/constants';

const getFilteredAssetsForLayerSummary = createSelector(
  [
    getAssetManagerFilters,
    getAssetManagerSelectedAssetTypeId,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    selectedAssetTypeId,
    filteredAssetsContext,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      assetStatusFilter,
      observationTypeFilter,
      tableFilter,
      selectedBandIds,
      multiSearchModeFilter,
      fieldFilters,
    } = assetManagerFilters;
    const configFieldFilter = filteredAssetsContext.locationSummarySlotConfig.fieldFilter;
    const hasConfigFieldFilter = configFieldFilter && Object.keys(configFieldFilter).length > 0;
    if (
      !searchTags
      && !dateFilter?.length
      && !selectedAssetTypeId
      && !assetStatusFilter?.ids?.length
      && !observationTypeFilter?.size
      && !hasConfigFieldFilter
    ) {
      return filteredAssetsContext.assetsArrayFromAssetType;
    }

    return getFilteredAssets(filteredAssetsContext, {
      searchTags,
      searchText,
      dateFilter,
      selectedAssetTypeId,
      selectedBandIds,
      assetStatusFilter,
      observationTypeFilter,
      tableFilter,
      multiSearchModeFilter,
      fieldFilters: { ...configFieldFilter, ...fieldFilters },
    });
  },
);

export const getAssetsKeyedByStatusForLayerSummary = createSelector(
  [getAssetTypes, getFilteredAssetsForLayerSummary],
  keyAssetsByStatus,
);

export const getSelectedLayerAncestorsForLayerSummary = createSelector(
  [
    getLayersKeyedByAncestorId,
    getAssetManagerFilters,
  ],
  (
    layersKeyedByAncestorId,
    assetManagerFilters,
  ) => {
    const {
      selectedLayerIds,
    } = assetManagerFilters;
    return getSelectedLayerAncestors(selectedLayerIds, layersKeyedByAncestorId);
  },
);

export const getFilteredAssetsKeyedByBandIdForLayerSummary = createSelector(
  [getPlannedTwinIDsKeyedByTwinId, getFilteredAssetsForLayerSummary],
  getAssetsKeyedByBandId,
);

export const getChartistDataForLayerSummary = createSelector(
  [
    map.presenterModeActive,
    getAssetManagerShowPlanView,
    getAssetStatusesKeyedById,
    getAssetsKeyedByStatusForLayerSummary,
    getPrimaryLayers,
    getLayersKeyedByAncestorId,
    getSelectedAssetType,
    getFilteredAssetsKeyedByBandIdForLayerSummary,
    getCurrentPlanBandsForCharts,
    getLayers,
    getAssetManagerFilters,
    getSelectedLayerAncestorsForLayerSummary,
    map.assetsKeyedById,
    getLocationSummarySlotConfig,
  ],
  (
    presenterModeActive,
    showPlanView,
    assetStatusesKeyedById,
    assetsKeyedByStatus,
    primaryLayers,
    layersKeyedByAncestorId,
    selectedAssetType,
    assetsKeyedByBandId,
    currentPlanBands,
    layers,
    assetManagerFilters,
    selectedLayerAncestors,
    assetsKeyedById,
    locationSummarySlotConfig,
  ) => {
    const { statuses } = selectedAssetType;
    const { selectedLayerIds, openedLayerId } = assetManagerFilters;
    const { slots: locationSlots } = locationSummarySlotConfig;
    const showNa = locationSlots.length > 0 && !openedLayerId;
    const effectiveLayerIDsForAsset = (
      asset: Asset,
    ) => getEffectiveLayerIDs(
      asset,
      locationSlots,
      assetsKeyedById as Map<string, Asset>,
    );

    const getStatus = () => {
      const series = statuses?.map((status) => {
        const statusId = status.id;
        const statusLabel = assetStatusesKeyedById.get(statusId)!?.label;
        let displayedLayer = layers;

        if (presenterModeActive && selectedLayerIds.length) {
          displayedLayer = layers.filter(({ id }) => selectedLayerAncestors.has(id));
        }

        const statusAssets = assetsKeyedByStatus.get(statusId) ?? [];
        const assetLayerIDs = statusAssets.map(effectiveLayerIDsForAsset);

        const newSubSeries = displayedLayer?.map((layer) => {
          const { id } = layer;
          const descendantLayers = layersKeyedByAncestorId.get(id) || [];
          const inScopeLayers = new Set([layer, ...descendantLayers].map(l => l.id));
          const count = assetLayerIDs.reduce((acc, effectiveLayerIDs) => (
            [...effectiveLayerIDs].some(lid => inScopeLayers.has(lid)) ? acc + 1 : acc
          ), 0);
          return { meta: statusLabel, value: count };
        }) ?? [];

        if (showNa) {
          const naCount = assetLayerIDs.reduce((acc, effectiveLayerIDs) => (
            effectiveLayerIDs.size === 0 ? acc + 1 : acc
          ), 0);
          newSubSeries.push({ meta: statusLabel, value: naCount });
        }

        return newSubSeries;
      });

      return series?.reverse() || [];
    };

    const getPlan = () => currentPlanBands.map((band) => {
      const assets = assetsKeyedByBandId.get(band.id) || [];
      let displayedLayer = layers;
      if (presenterModeActive && selectedLayerIds.length) {
        displayedLayer = layers.filter(({ id }) => selectedLayerAncestors.has(id));
      }

      const assetLayerIDs = assets.map(effectiveLayerIDsForAsset);

      const newSubSeries = displayedLayer?.map((layer) => {
        const { id } = layer;
        const descendantLayers = layersKeyedByAncestorId.get(id) || [];
        const inScopeLayers = new Set([layer, ...descendantLayers].map(l => l.id));
        const count = assetLayerIDs.reduce((acc, effectiveLayerIDs) => (
          [...effectiveLayerIDs].some(lid => inScopeLayers.has(lid)) ? acc + 1 : acc
        ), 0);
        return { meta: band.label, value: count };
      }) ?? [];

      if (showNa) {
        const naCount = assetLayerIDs.reduce((acc, effectiveLayerIDs) => (
          effectiveLayerIDs.size === 0 ? acc + 1 : acc
        ), 0);
        newSubSeries.push({ meta: band.label, value: naCount });
      }

      return newSubSeries;
    }).reverse();

    const displayedLayers = primaryLayers?.length > 0
      ? layers?.filter(
        ({ id }) => (
          presenterModeActive && selectedLayerIds.length
            ? selectedLayerAncestors.has(id)
            : true
        ),
      ) : [];
    const labels = [
      ...displayedLayers.map(({ label }) => label),
      ...(showNa && displayedLayers.length > 0 ? [UNLOCATED_LAYER_LABEL] : []),
    ];

    return {
      labels,
      series: showPlanView ? getPlan() : getStatus(),
    };
  },
);
